import { S3_CDN_DOMAIN } from "../constants";

const YOUTUBE_ID_REGEX =
  /(?:https?:\/\/)?(?:www\.|m\.)?(?:youtube\.com\/(?:[^/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|\S*?[?&]v=|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;

/** @param {string} url */
function stripProtocolAndWww(url) {
  return url.replace(/^https?:\/\//i, "").replace(/^www\./i, "");
}

/** @param {string} url */
function ensureProtocol(url) {
  return /^https?:\/\//i.test(url) ? url : `https://${url}`;
}

/** @param {string} url */
function hostFromUrl(url) {
  try {
    return new URL(ensureProtocol(url)).hostname.replace(/^www\./i, "");
  } catch {
    return stripProtocolAndWww(url);
  }
}

const VIMEO_ID_REGEX = /^(?:www\.)?vimeo\.com\/(?:video\/)?(\d+)/i;
const LOOM_SHARE_REGEX = /^(?:www\.)?loom\.com\/(?:share|embed)\/([a-z0-9-]+)/i;

/**
 * Embeddable player url for the generic links we know how to play inline (Vimeo, Loom).
 *
 * @param {string} url
 * @returns {string|null}
 */
function embedUrlForLink(url) {
  const bare = stripProtocolAndWww(url);
  const vimeoId = VIMEO_ID_REGEX.exec(bare)?.[1];
  if (vimeoId) {
    return `https://player.vimeo.com/video/${vimeoId}?autoplay=1`;
  }
  const loomId = LOOM_SHARE_REGEX.exec(bare)?.[1];
  if (loomId) {
    return `https://www.loom.com/embed/${loomId}?autoplay=1`;
  }
  return null;
}

/** @param {string} path */
function fileNameFromPath(path) {
  return path.split(/[\\/]/).pop() || path;
}

/**
 * @param {string|null|undefined} url
 * @returns {string|null} 11-char YouTube video id, or null when it is not a YouTube link.
 */
export function getYoutubeVideoId(url) {
  if (typeof url !== "string") {
    return null;
  }
  return YOUTUBE_ID_REGEX.exec(url.trim())?.[1] ?? null;
}

/** @param {string} videoId */
export function youtubeThumbnailUrl(videoId) {
  return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
}

/**
 * Uploaded library videos are served as `.mp4` from the video CDN, keyed by athlete.
 *
 * @param {string} athleteId
 * @param {string} videoOriginal
 */
export function uploadedVideoUrl(athleteId, videoOriginal) {
  const mp4Name = videoOriginal.replace(/\.[^/.]+$/, ".mp4");
  return `${S3_CDN_DOMAIN}other_exercise_videos/${athleteId}/${mp4Name}`;
}

/**
 * Describes the video the preview card should show, from the metadata draft.
 * Same precedence as the draft inputs: a picked file, then a saved upload, then a URL.
 *
 * `badge` is the source name shown on the thumbnail; it is empty for uploads (the component
 * shows a translated label) and for no video.
 *
 * `playUrl` is null when the video cannot be played inline: a link that is not YouTube,
 * Vimeo or Loom, or a freshly picked file: its bytes only exist in the browser,
 * so the component builds an object URL from the `File` (a link then offers only "Open link").
 *
 * @param {{ videoUrl?: string, videoFile?: string|null, videoOriginal?: string, athleteId?: string }} values
 * @returns {{
 *   kind: "none" | "youtube" | "upload" | "link",
 *   badge: string,
 *   displayName: string,
 *   videoId: string|null,
 *   openUrl: string|null,
 *   playUrl: string|null,
 * }}
 */
export function resolveVideoSource({
  videoUrl,
  videoFile,
  videoOriginal,
  athleteId,
} = {}) {
  const url = typeof videoUrl === "string" ? videoUrl.trim() : "";

  if (videoFile) {
    return {
      kind: "upload",
      badge: "",
      displayName: fileNameFromPath(videoFile),
      videoId: null,
      openUrl: null,
      playUrl: null,
    };
  }

  if (videoOriginal) {
    return {
      kind: "upload",
      badge: "",
      displayName: videoOriginal,
      videoId: null,
      openUrl: null,
      playUrl: athleteId ? uploadedVideoUrl(athleteId, videoOriginal) : null,
    };
  }

  if (!url) {
    return {
      kind: "none",
      badge: "",
      displayName: "",
      videoId: null,
      openUrl: null,
      playUrl: null,
    };
  }

  const videoId = getYoutubeVideoId(url);
  if (videoId) {
    return {
      kind: "youtube",
      badge: "YouTube",
      displayName: stripProtocolAndWww(url),
      videoId,
      openUrl: `https://www.youtube.com/watch?v=${videoId}`,
      playUrl: `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`,
    };
  }

  return {
    kind: "link",
    badge: hostFromUrl(url),
    displayName: stripProtocolAndWww(url),
    videoId: null,
    openUrl: ensureProtocol(url),
    playUrl: embedUrlForLink(url),
  };
}

/**
 * @param {number|null|undefined} seconds
 * @returns {string|null} "m:ss" or "h:mm:ss"; null when the value is not a usable duration.
 */
export function formatDuration(seconds) {
  if (typeof seconds !== "number" || !Number.isFinite(seconds) || seconds < 0) {
    return null;
  }
  const total = Math.round(seconds);
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const secs = String(total % 60).padStart(2, "0");
  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, "0")}:${secs}`;
  }
  return `${minutes}:${secs}`;
}

/**
 * @param {number|null|undefined} bytes
 * @returns {string|null} "14 MB" / "820 KB"; null when the size is unknown.
 */
export function formatFileSize(bytes) {
  if (typeof bytes !== "number" || !Number.isFinite(bytes) || bytes < 0) {
    return null;
  }
  const megabytes = bytes / (1024 * 1024);
  if (megabytes >= 1) {
    return `${Math.round(megabytes)} MB`;
  }
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}
